import { BrandPalette, BrandColorSwatch, VdpTemplate, VdpElement } from '../types';

export const DEFAULT_BRAND_PALETTE: BrandPalette = {
  name: 'Modern Corporate',
  primary: '#2563eb', // Royal Blue
  secondary: '#10b981', // Emerald Green
  accent: '#f59e0b', // Amber
  background: '#ffffff', // Clean White
  neutral: '#0f172a', // Slate Dark
  swatches: [
    { id: 'sw-1', name: 'Brand Navy', hex: '#1e40af', type: 'custom' },
    { id: 'sw-2', name: 'Brand Mint', hex: '#6ee7b7', type: 'custom' },
    { id: 'sw-3', name: 'Alert Crimson', hex: '#ef4444', type: 'custom' },
    { id: 'sw-4', name: 'Muted Gray', hex: '#64748b', type: 'custom' },
  ],
};

export interface BrandPalettePreset {
  id: string;
  name: string;
  description: string;
  palette: BrandPalette;
}

export const BRAND_PALETTE_PRESETS: BrandPalettePreset[] = [
  {
    id: 'modern-corporate',
    name: 'Modern Corporate',
    description: 'Clean high-trust blue and energetic emerald for professional business labels',
    palette: {
      name: 'Modern Corporate',
      primary: '#2563eb',
      secondary: '#10b981',
      accent: '#f59e0b',
      background: '#ffffff',
      neutral: '#0f172a',
      swatches: [
        { id: 'c1', name: 'Navy 900', hex: '#1e3a8a', type: 'custom' },
        { id: 'c2', name: 'Sky 400', hex: '#38bdf8', type: 'custom' },
        { id: 'c3', name: 'Slate 500', hex: '#64748b', type: 'custom' },
      ],
    },
  },
  {
    id: 'vibrant-sunset',
    name: 'Sunset Gold & Rose',
    description: 'Warm, high-energy palette for gourmet, bakery, and premium cosmetics',
    palette: {
      name: 'Sunset Gold & Rose',
      primary: '#e11d48', // Rose Red
      secondary: '#d97706', // Amber Gold
      accent: '#fbbf24', // Warm Sunlight
      background: '#fffbeb', // Warm Off-White
      neutral: '#1c1917', // Stone Obsidian
      swatches: [
        { id: 's1', name: 'Deep Wine', hex: '#881337', type: 'custom' },
        { id: 's2', name: 'Peach', hex: '#fdba74', type: 'custom' },
        { id: 's3', name: 'Warm Cream', hex: '#fef3c7', type: 'custom' },
      ],
    },
  },
  {
    id: 'botanical-organic',
    name: 'Eco Forest & Olive',
    description: 'Earthy botanical tones for organic foods, candles, and natural goods',
    palette: {
      name: 'Eco Forest & Olive',
      primary: '#15803d', // Forest Green
      secondary: '#b45309', // Amber Terracotta
      accent: '#84cc16', // Lime Leaf
      background: '#fcfdfa', // Natural Tint
      neutral: '#142617', // Dark Forest
      swatches: [
        { id: 'b1', name: 'Sage Green', hex: '#4ade80', type: 'custom' },
        { id: 'b2', name: 'Earthy Clay', hex: '#78350f', type: 'custom' },
        { id: 'b3', name: 'Kraft Tan', hex: '#d7c4a3', type: 'custom' },
      ],
    },
  },
  {
    id: 'cyber-electric',
    name: 'Cyber Violet & Cyan',
    description: 'High-contrast futuristic electric theme for gaming, electronics, and festivals',
    palette: {
      name: 'Cyber Violet & Cyan',
      primary: '#8b5cf6', // Electric Purple
      secondary: '#06b6d4', // Neon Cyan
      accent: '#ec4899', // Hot Pink
      background: '#09090b', // Deep Charcoal Dark
      neutral: '#f4f4f5', // Crisp Light
      swatches: [
        { id: 'cy1', name: 'Ultraviolet', hex: '#7c3aed', type: 'custom' },
        { id: 'cy2', name: 'Aqua Spark', hex: '#22d3ee', type: 'custom' },
        { id: 'cy3', name: 'Fluo Yellow', hex: '#facc15', type: 'custom' },
      ],
    },
  },
  {
    id: 'luxury-noir',
    name: 'Luxury Champagne & Noir',
    description: 'Opulent gold metallic tones and deep obsidian for high-end packaging',
    palette: {
      name: 'Luxury Champagne & Noir',
      primary: '#ca8a04', // Champagne Gold
      secondary: '#475569', // Slate Platinum
      accent: '#eab308', // Bright Gold
      background: '#0f172a', // Midnight Slate
      neutral: '#f8fafc', // Platinum White
      swatches: [
        { id: 'lx1', name: 'Pure Gold', hex: '#eab308', type: 'custom' },
        { id: 'lx2', name: 'Bronze', hex: '#92400e', type: 'custom' },
        { id: 'lx3', name: 'Matte Charcoal', hex: '#1e293b', type: 'custom' },
      ],
    },
  },
  {
    id: 'medical-teal',
    name: 'Clinical Teal & Indigo',
    description: 'Clean, sterile, high-legibility clinical aesthetic for pharmaceutical and lab stickers',
    palette: {
      name: 'Clinical Teal & Indigo',
      primary: '#0d9488', // Medical Teal
      secondary: '#4f46e5', // Deep Indigo
      accent: '#0284c7', // Cyan Blue
      background: '#ffffff', // Pure White
      neutral: '#0f172a', // Slate Dark
      swatches: [
        { id: 'md1', name: 'Mint Clean', hex: '#2dd4bf', type: 'custom' },
        { id: 'md2', name: 'Warning Orange', hex: '#f97316', type: 'custom' },
        { id: 'md3', name: 'Steel Gray', hex: '#94a3b8', type: 'custom' },
      ],
    },
  },
  {
    id: 'minimal-mono',
    name: 'Nordic Monochrome',
    description: 'Timeless architectural minimalism with crisp black, white, and graphite accents',
    palette: {
      name: 'Nordic Monochrome',
      primary: '#000000', // Pure Black
      secondary: '#52525b', // Zinc Graphite
      accent: '#71717a', // Medium Zinc
      background: '#ffffff', // Pure White
      neutral: '#18181b', // Zinc Dark
      swatches: [
        { id: 'mn1', name: 'Pure Black', hex: '#000000', type: 'custom' },
        { id: 'mn2', name: 'Pure White', hex: '#ffffff', type: 'custom' },
        { id: 'mn3', name: 'Silver Slate', hex: '#cbd5e1', type: 'custom' },
      ],
    },
  },
];

/**
 * Validates whether a given string is a valid 3-digit or 6-digit hex color.
 */
export const isValidHex = (hex: string): boolean => {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
};

/**
 * Normalizes a hex color string to lowercase standard format.
 */
export const normalizeHex = (hex: string): string => {
  if (!hex) return '#000000';
  let clean = hex.trim();
  if (!clean.startsWith('#')) clean = `#${clean}`;
  return clean;
};

/**
 * Generates CSS Variables string for a BrandPalette.
 */
export const exportPaletteAsCss = (palette: BrandPalette): string => {
  let css = `:root {\n`;
  css += `  --brand-primary: ${palette.primary};\n`;
  css += `  --brand-secondary: ${palette.secondary};\n`;
  if (palette.accent) css += `  --brand-accent: ${palette.accent};\n`;
  if (palette.background) css += `  --brand-background: ${palette.background};\n`;
  if (palette.neutral) css += `  --brand-neutral: ${palette.neutral};\n`;
  if (palette.swatches) {
    palette.swatches.forEach((s) => {
      const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      css += `  --brand-color-${slug}: ${s.hex};\n`;
    });
  }
  css += `}\n`;
  return css;
};

/**
 * Intelligently applies brand palette styling across template elements.
 */
export const applyBrandPaletteToTemplate = (
  template: VdpTemplate,
  palette: BrandPalette,
  options: {
    updateBackground?: boolean;
    updateElements?: boolean;
  } = { updateBackground: true, updateElements: true }
): VdpTemplate => {
  const newTemplate: VdpTemplate = {
    ...template,
    brandPalette: palette,
    backgroundColor: options.updateBackground && palette.background ? palette.background : template.backgroundColor,
  };

  if (!options.updateElements || !template.elements.length) {
    return newTemplate;
  }

  // Assign primary/secondary/neutral colors based on element roles
  newTemplate.elements = template.elements.map((el, index) => {
    const updated: VdpElement = { ...el };

    if (el.type === 'text' || el.type === 'uppercase') {
      // Main header / first text element gets Primary Brand
      if (index === 0 || (el.fontSize && el.fontSize >= 14) || el.isBold) {
        updated.color = palette.primary;
        updated.fillColor = palette.primary;
      } else {
        // Body / secondary text gets Neutral Dark
        updated.color = palette.neutral || '#0f172a';
        updated.fillColor = palette.neutral || '#0f172a';
      }
    } else if (el.type === 'currency' || el.type === 'serial') {
      // Dynamic numbers get Primary or Accent Brand
      updated.color = palette.primary;
      updated.fillColor = palette.primary;
    } else if (el.type === 'date') {
      updated.color = palette.secondary;
      updated.fillColor = palette.secondary;
    } else if (el.type === 'rectangle' || el.type === 'circle' || el.type === 'star' || el.type === 'polygon') {
      // For border outlines or solid backgrounds
      if (el.borderColor && el.borderColor !== 'transparent' && el.borderColor !== 'none') {
        updated.borderColor = palette.primary;
        updated.strokeColor = palette.primary;
      }
      if (el.color && el.color !== 'transparent' && el.color !== 'none') {
        updated.color = index % 2 === 0 ? palette.primary : palette.secondary;
        updated.fillColor = updated.color;
      }
    } else if (el.type === 'line' || el.type === 'path' || el.type === 'pen') {
      updated.strokeColor = palette.primary;
      updated.borderColor = palette.primary;
    }

    return updated;
  });

  return newTemplate;
};
